// src/utils/auth.js
import { STORAGE_KEYS } from './constants';

/**
 * Get the authentication token from localStorage
 * @returns {string|null} The token or null if not found
 */
export const getToken = () => {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Get a valid token (not expired)
 * @returns {string|null} Valid token or null
 */
export const getValidToken = () => {
  const token = getToken();
  if (!token) return null;

  try {
    // Decode JWT token to check expiration
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;

    // Check if token is expired
    if (payload.exp && payload.exp < currentTime) {
      removeToken();
      return null;
    }

    return token;
  } catch (error) {
    console.error('Error validating token:', error);
    removeToken();
    return null;
  }
};

/**
 * Set the authentication token and refresh token
 * @param {string} token - The JWT token
 * @param {string} refreshToken - The refresh token
 */
export const setToken = (token, refreshToken = null) => {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  }
};

/**
 * Remove the authentication token and related data
 */
export const removeToken = () => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem('refreshToken');
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
};

/**
 * Get user data from localStorage
 * @returns {Object|null} User data or null
 */
export const getStoredUser = () => {
  try {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing stored user data:', error);
    return null;
  }
};

/**
 * Store user data in localStorage
 * @param {Object} userData - The user data to store
 */
export const setStoredUser = (userData) => {
  try {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  } catch (error) {
    console.error('Error storing user data:', error);
  }
};

/**
 * Check if user has specific role
 * @param {Object} user - User object
 * @param {string} role - Role to check
 * @returns {boolean} True if user has role
 */
export const hasRole = (user, role) => {
  if (!user || !user.roles) return false;
  return user.roles.includes(role) || user.roles.includes('hybrid');
};

/**
 * Check if user is authenticated based on stored data
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
  const token = getValidToken();
  return !!token;
};

/**
 * Format user display name
 * @param {Object} user - User object
 * @returns {string} Formatted display name
 */
export const getUserDisplayName = (user) => {
  if (!user) return 'User';
  
  if (user.displayName) return user.displayName;
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  if (user.firstName) return user.firstName;
  if (user.username) return user.username;
  
  return 'User';
};

/**
 * Get user avatar URL or initials
 * @param {Object} user - User object
 * @returns {string} Avatar URL or initials
 */
export const getUserAvatar = (user) => {
  if (!user) return 'U';
  
  if (user.avatar) return user.avatar;
  
  // Generate initials
  const firstName = user.firstName || user.username || 'U';
  const lastName = user.lastName || '';
  
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

/**
 * Check if user's phone is verified
 * @param {Object} user - User object
 * @returns {boolean} True if phone is verified
 */
export const isPhoneVerified = (user) => {
  return Boolean(user?.phoneVerified || user?.verification?.phone?.verified || false);
};

/**
 * Check if user's email is verified
 * @param {Object} user - User object
 * @returns {boolean} True if email is verified
 */
export const isEmailVerified = (user) => {
  return Boolean(user?.emailVerified || user?.verification?.email?.verified || false);
};

/**
 * Get user's current level
 * @param {Object} user - User object
 * @returns {number} User level
 */
export const getUserLevel = (user) => {
  return user?.webLevel ?? user?.ranking?.level ?? 1;
};

/**
 * Get user's commission rate
 * @param {Object} user - User object
 * @returns {number} Commission rate percentage
 */
export const getCommissionRate = (user) => {
  return user?.ranking?.commissionRate || 20;
};

/**
 * Format phone number for display
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format Ghana numbers
  if (cleaned.startsWith('233')) {
    return `+${cleaned}`;
  } else if (cleaned.startsWith('0') && cleaned.length === 10) {
    return `+233${cleaned.substring(1)}`;
  }
  
  return phone;
};

/**
 * Check if user account is locked
 * @returns {boolean} True if account is locked
 */
export const isAccountLocked = () => {
  const lockoutTime = localStorage.getItem('gigship_lockout');
  if (!lockoutTime) return false;
  
  const lockoutExpiry = parseInt(lockoutTime);
  return Date.now() < lockoutExpiry;
};

/**
 * Get remaining lockout time in minutes
 * @returns {number} Remaining minutes
 */
export const getRemainingLockoutTime = () => {
  const lockoutTime = localStorage.getItem('gigship_lockout');
  if (!lockoutTime) return 0;
  
  const lockoutExpiry = parseInt(lockoutTime);
  const remaining = Math.max(0, lockoutExpiry - Date.now());
  return Math.ceil(remaining / 60000); // Convert to minutes
};

/**
 * Clear account lockout
 */
export const clearAccountLockout = () => {
  localStorage.removeItem('gigship_lockout');
};

export default {
  getToken,
  getValidToken,
  setToken,
  removeToken,
  getStoredUser,
  setStoredUser,
  hasRole,
  isAuthenticated,
  getUserDisplayName,
  getUserAvatar,
  isPhoneVerified,
  isEmailVerified,
  getUserLevel,
  getCommissionRate,
  formatPhoneNumber,
  isAccountLocked,
  getRemainingLockoutTime,
  clearAccountLockout
};